// StockSimulation.tsx - Main Component
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Card, CardContent, Typography } from '@mui/material';
import { ShowChart as StockIcon } from '@mui/icons-material';
import type { RootState, AppDispatch } from 'app/store';
import {
  runSimulation,
  SimulationPayload
} from 'slices/fundManagementInvestment/simulationDashboardSlice';

// Import modular components
import StockResults from './StockResults';
import { StockForm, ExpandedSections, SectionKey, SimulationResult } from '../simulationTypes';
import StockFormComponent from './StockFormComponent';

const StockSimulation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, result } = useSelector((s: RootState) => s.simulationDashboard);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    basic: true,
    trading: false,
    dividends: false,
    market: false,
    risk: false
  });

  const [form, setForm] = useState<StockForm>({
    iterations: 10000,
    years: 12,
    assetId: 'stock_asx200_001',
    assetName: 'ASX 200 Index Fund',
    purchasePrice: 150000,
    brokerage: 750,
    platformFees: 500,
    setupCosts: 250,
    annualIncomeAmount: 6000,
    incomeGrowthMin: 0.03,
    incomeGrowthMax: 0.06,
    managementFeePercent: 0.015,
    transactionCostPercent: 0.005,
    capitalGrowthMin: 0.07,
    capitalGrowthMax: 0.13,
    volatilityStdDev: 0.22,
    recessionProb: 0.15,
    recessionImpact: -0.35,
    defaultProbability: 0.005,
    liquidityDiscount: 0.02
  });

  const handleInputChange = (field: keyof StockForm, value: number | string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Build API payload
  const buildPayload = (): SimulationPayload => ({
    simulation_config: {
      iterations: form.iterations,
      years: form.years,
      percentile_choices: ['P10', 'P50', 'P90']
    },
    assets: [
      {
        asset_id: form.assetId,
        asset_name: form.assetName,
        asset_type: 'stock' as const,
        initial_investment: {
          purchase_price: form.purchasePrice,
          upfront_costs: {
            brokerage: form.brokerage,
            platform_fees: form.platformFees,
            setup_costs: form.setupCosts
          }
        },
        income_streams: {
          primary_income: {
            amount: form.annualIncomeAmount,
            frequency: 'annual' as const,
            growth_rate: {
              type: 'range',
              min: form.incomeGrowthMin,
              max: form.incomeGrowthMax
            }
          }
        },
        expenses: {
          variable_percent: {
            management_fee_percent: form.managementFeePercent,
            transaction_cost_percent: form.transactionCostPercent
          }
        },
        market_assumptions: {
          capital_growth: {
            type: 'range',
            min: form.capitalGrowthMin,
            max: form.capitalGrowthMax
          },
          volatility: {
            annual_std_deviation: form.volatilityStdDev
          },
          market_cycles: {
            recession_probability: form.recessionProb,
            recession_impact: form.recessionImpact
          }
        },
        risk_factors: {
          default_probability: form.defaultProbability,
          liquidity_discount: form.liquidityDiscount
        }
      }
    ]
  });

  const runStockSimulation = () => {
    const payload = buildPayload();
    dispatch(runSimulation(payload));
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1.5 } }}>
      <Grid container spacing={2}>
        {/* Form - 30% */}
        <Grid item xs={12} lg={3.5}>
          <StockFormComponent
            form={form}
            handleInputChange={handleInputChange}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            runStockSimulation={runStockSimulation}
            loading={loading}
            error={error}
          />
        </Grid>

        {/* Results - 70% */}
        <Grid item xs={12} lg={8.5}>
          {result ? (
            <StockResults
              result={result as SimulationResult}
              form={form}
              reportRef={reportRef as React.RefObject<HTMLDivElement>}
              exportMenuAnchor={exportMenuAnchor}
              setExportMenuAnchor={setExportMenuAnchor}
            />
          ) : (
            <Card
              elevation={1}
              sx={{
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <StockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Stock Market Simulator Ready
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure parameters and run simulation for comprehensive stock market analysis
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockSimulation;
